import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Loader2, Megaphone, Tag, Copy } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import apiService from '../services/api.service';
import { toast } from 'sonner';

interface AppPromo {
  id: string;
  code: string;
  title_fr: string;
  title_en: string;
  title_nl: string;
  title_es: string;
  subtitle_fr: string;
  subtitle_en: string;
  subtitle_nl: string;
  subtitle_es: string;
  badge_fr: string;
  badge_en: string;
  badge_nl: string;
  badge_es: string;
  discount_text_fr: string;
  discount_text_en: string;
  discount_text_nl: string;
  discount_text_es: string;
  is_active: boolean;
  created_at: string;
}

const emptyForm = {
  code: '',
  title_fr: '', title_en: '', title_nl: '', title_es: '',
  subtitle_fr: '', subtitle_en: '', subtitle_nl: '', subtitle_es: '',
  badge_fr: 'PROMO', badge_en: 'PROMO', badge_nl: 'PROMO', badge_es: 'PROMO',
  discount_text_fr: '', discount_text_en: '', discount_text_nl: '', discount_text_es: '',
  is_active: false,
};

const AdminAppPromos: React.FC = () => {
  const { t } = useTranslation();
  const [promos, setPromos] = useState<AppPromo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<AppPromo | null>(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/subscription/promos');
      setPromos(res.data || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('appPromos.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingPromo(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (promo: AppPromo) => {
    setEditingPromo(promo);
    setForm({
      code: promo.code,
      title_fr: promo.title_fr, title_en: promo.title_en,
      title_nl: promo.title_nl, title_es: promo.title_es,
      subtitle_fr: promo.subtitle_fr, subtitle_en: promo.subtitle_en,
      subtitle_nl: promo.subtitle_nl, subtitle_es: promo.subtitle_es,
      badge_fr: promo.badge_fr, badge_en: promo.badge_en,
      badge_nl: promo.badge_nl, badge_es: promo.badge_es,
      discount_text_fr: promo.discount_text_fr, discount_text_en: promo.discount_text_en,
      discount_text_nl: promo.discount_text_nl, discount_text_es: promo.discount_text_es,
      is_active: promo.is_active,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim() || !form.title_fr.trim() || !form.title_en.trim()) return;
    try {
      if (editingPromo) {
        await apiService.put(`/subscription/promos/${editingPromo.id}`, form);
        toast.success(t('appPromos.updated'));
      } else {
        await apiService.post('/subscription/promos', form);
        toast.success(t('appPromos.created'));
      }
      setDialogOpen(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('appPromos.saveError'));
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await apiService.delete(`/subscription/promos/${id}`);
      await loadData();
      toast.success(t('appPromos.deleted'));
    } catch (err: any) {
      setError(err.response?.data?.detail || t('appPromos.deleteError'));
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl md:text-3xl font-bold">{t('appPromos.title')}</h1>
        <Button size="sm" onClick={openCreate} className="w-full sm:w-auto">
          <Plus size={16} className="mr-1" />
          {t('appPromos.create')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 size={32} className="animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('appPromos.code')}</TableHead>
                  <TableHead>{t('appPromos.title_field')} (FR)</TableHead>
                  <TableHead>{t('appPromos.discountText')} (FR)</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead>{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      {t('appPromos.empty')}
                    </TableCell>
                  </TableRow>
                ) : (
                  promos.map((promo) => (
                    <TableRow key={promo.id} className={!promo.is_active ? 'opacity-50' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Tag size={14} className="text-muted-foreground" />
                          <span className="font-mono font-bold">{promo.code}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(promo.code);
                              toast.success(promo.code + ' copied');
                            }}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[250px] truncate">
                        {promo.title_fr}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {promo.discount_text_fr}
                      </TableCell>
                      <TableCell>
                        <Badge className={promo.is_active ? 'bg-success/15 text-success' : 'bg-white/[0.06] text-muted-foreground'}>
                          {promo.is_active ? t('appPromos.active') : t('appPromos.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(promo)}>
                            <Edit size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeactivate(promo.id)}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingPromo ? t('appPromos.edit') : t('appPromos.create')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Code + Active */}
            <div>
              <Label>{t('appPromos.code')}</Label>
              <Input
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="EARLYBIRDS"
                className="font-mono"
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={v => setForm({ ...form, is_active: v })}
              />
              <Label>{form.is_active ? t('appPromos.active') : t('appPromos.inactive')}</Label>
            </div>

            {/* Translations by language */}
            <Tabs defaultValue="fr">
              <TabsList className="w-full">
                <TabsTrigger value="fr" className="flex-1">FR</TabsTrigger>
                <TabsTrigger value="en" className="flex-1">EN</TabsTrigger>
                <TabsTrigger value="nl" className="flex-1">NL</TabsTrigger>
                <TabsTrigger value="es" className="flex-1">ES</TabsTrigger>
              </TabsList>

              {(['fr', 'en', 'nl', 'es'] as const).map(lang => (
                <TabsContent key={lang} value={lang} className="space-y-3 mt-3">
                  <div>
                    <Label>{t('appPromos.badge')}</Label>
                    <Input
                      value={(form as any)[`badge_${lang}`]}
                      onChange={e => setForm({ ...form, [`badge_${lang}`]: e.target.value })}
                      placeholder="LAUNCH OFFER"
                    />
                  </div>
                  <div>
                    <Label>{t('appPromos.title_field')}</Label>
                    <Input
                      value={(form as any)[`title_${lang}`]}
                      onChange={e => setForm({ ...form, [`title_${lang}`]: e.target.value })}
                      placeholder="First 200 users: -10€ on annual plan"
                    />
                  </div>
                  <div>
                    <Label>{t('appPromos.discountText')}</Label>
                    <Input
                      value={(form as any)[`discount_text_${lang}`]}
                      onChange={e => setForm({ ...form, [`discount_text_${lang}`]: e.target.value })}
                      placeholder="€29.99 instead of €39.99"
                    />
                  </div>
                  <div>
                    <Label>{t('appPromos.subtitle_field')}</Label>
                    <Input
                      value={(form as any)[`subtitle_${lang}`]}
                      onChange={e => setForm({ ...form, [`subtitle_${lang}`]: e.target.value })}
                      placeholder="Use code EARLYBIRDS in the App Store"
                    />
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={!form.code.trim()}>
              {editingPromo ? t('common.save') : t('appPromos.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAppPromos;
